import type { DispatchAssignmentRule } from '@/services/dispatch';

/**
 * Routing-rule "intelligence" — proactively catch misconfigurations that
 * would silently mis-route shipments. The headline case: a higher-priority
 * broad rule that SHADOWS a more specific lower-priority rule, so the lower
 * one never fires (e.g. an "All destinations" rule above an "International →
 * DHL" rule sends international to the wrong carrier).
 */

/** Does `higher` match every shipment `lower` would — making `lower`
 *  unreachable when `higher` sits above it in priority order? */
export function ruleCovers(
  higher: DispatchAssignmentRule,
  lower: DispatchAssignmentRule,
): boolean {
  // Tier: higher must apply to lower's tier (null = any tier).
  if (higher.tier && higher.tier !== lower.tier) return false;
  // Destination scope: ALL is a superset of everything; otherwise must match.
  if (higher.scope !== 'ALL' && higher.scope !== lower.scope) return false;
  // List matchers: higher is broader iff it's a wildcard (empty) OR its list
  // already contains every value lower constrains on.
  const superset = (h: string[], l: string[]) =>
    h.length === 0 || (l.length > 0 && l.every((x) => h.includes(x)));
  return (
    superset(higher.countryIn, lower.countryIn) &&
    superset(higher.stateIn, lower.stateIn) &&
    superset(higher.warehouseCodeIn, lower.warehouseCodeIn)
  );
}

/**
 * For each ACTIVE rule, the higher-priority ACTIVE rule that shadows it
 * (renders it unreachable), or undefined. Considers only the supplied rule
 * set, so it works on the full list or a tier-scoped subset.
 */
export function findShadowedRules(
  rules: DispatchAssignmentRule[],
): Map<string, DispatchAssignmentRule> {
  const active = rules
    .filter((r) => r.status === 'ACTIVE')
    .slice()
    .sort((a, b) => a.priority - b.priority);
  const shadows = new Map<string, DispatchAssignmentRule>();
  for (let i = 0; i < active.length; i++) {
    for (let j = 0; j < i; j++) {
      if (ruleCovers(active[j], active[i])) {
        shadows.set(active[i].id, active[j]);
        break;
      }
    }
  }
  return shadows;
}
